import React, { useMemo } from 'react';
import { CartItem, Product, TrackingAddToCartAddFromType } from 'types/schema';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import LazyImage from 'components/Image/LazyImage';
import noImage from '../icons/noIMG.svg';
import DeleteIcon from '../icons/deleteIcon.svg';
import NoteIcon from '../icons/noteIcon';
import { FrameImage } from 'components/Product/ProductImagesModal';
import useProductUomInfo from 'hooks/useProductUomInfo';
import Money from 'components/Money/Money';
import calculateVAT from 'utils/calculateVAT';
import QuantityInput from 'containers/QuantityInput';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import translations from 'translations';
import messages from 'utils/messages';
import generateProductSlug from 'utils/generateProductSlug';

const ProductItemContainer = styled.div`
  padding: 16px 20px;
  background: #fff;
  display: grid;
  grid-template-columns: auto 140px 120px 120px;
  column-gap: 16px;
  position: relative;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
      grid-template-columns: auto 100px 120px 120px;
    `}

  ${({ isLastItem }) =>
    isLastItem &&
    `
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
     `}
`;

const ProductItemInfoWrapper = styled.div`
  display: flex;
  gap: 12px;
  padding-right: 24px;
`;

const ProductItemImage = styled.div<any>`
  position: relative;
  overflow: hidden;
  background: #ffffff;
  border-radius: 4px;
  display: inline-block;
  height: 80px;
  width: 80px;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
    width: 40px;
    height: 40px;
    `}
`;

const ProductInfoDetailContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #111827;
    `}
`;

const ProductUom = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
  font-weight: 500;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
      line-height: 18px;
      font-weight: 500;
    `}
`;

const ProductPrice = styled.div`
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  color: #111827;
  font-weight: 600;
`;

const ProductNoteContainer = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const ProductNote = styled.div`
  flex: 1;
  word-break: break-all;
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
  font-weight: 500;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const ProductOriginalPrice = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: #9ca3af;
  font-weight: 400;
  text-decoration: line-through;
  margin-left: 4px;
`;

const ProductQuantityContainer = styled.div`
  display: flex;
  align-items: center;
`;

const QuantityValue = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: #303643;
  font-weight: 400;
`;

const MoneyBeforeVAT = styled.div`
  font-size: 16px;
  line-height: 20px;
  color: #111827;
  font-weight: 400;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 16px;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
      align-items: flex-start;
      font-size: 14px;
      font-weight: 400;
      padding-top: 4px;
    `}
`;

const TotalAfterVATContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
      justify-content: flex-start;
    `}
`;

const TotalAfterVAT = styled.div`
  font-size: 18px;
  line-height: 26px;
  color: #111827;
  font-weight: 600;

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
      font-size: 16px;
      line-height: 24px;
      color: #303643;
    `}
`;

const VATText = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
  font-weight: 400;
`;

const ProductLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover {
    color: inherit;
  }
`;

const DeleteProductContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 20px;
  cursor: pointer;
  padding: 4px;
  border-radius: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d5d7d5;
  background: #fff;

  &:hover {
    background: #f5f5f4;
  }
`;

const ItemImage = styled(({ noImage, ...rest }) => <LazyImage {...rest} />)<any>`
  width: 80px;
  height: 80px;
  object-fit: ${({ noImage }) => (noImage ? 'contain' : 'cover')};
  ${({ noImage }) => (noImage ? `opacity: 0.3;` : '')};

  ${({ isFinalStep }) =>
    isFinalStep &&
    `
    width: 40px;
    height: 40px;
    `}
`;

interface CartItemDesktopProps {
  cartItem: CartItem;
  lang: string;
  onRemoveProduct?: (item: CartItem) => void;
  openNote?: () => void;
  itemDiscountBeforeVat: number;
  isFinalStep: boolean;
  isLastItem: boolean;
}

const CartItemDesktop = (props: CartItemDesktopProps) => {
  const loggedIn = !!window.localStorage.getItem('token');
  const { cartItem, lang, itemDiscountBeforeVat, onRemoveProduct, isFinalStep, isLastItem, openNote } = props;

  const { uomInfo } = useProductUomInfo(cartItem?.product as Product);

  const frameTagImage = lang === 'vi' ? cartItem?.product?.tagImageVn : cartItem?.product?.tagImageEn || '';

  const pdpUrl = `/products/${generateProductSlug(cartItem?.product?.name || '', cartItem?.product?.supplierInternalCode || '', lang)}`;

  const totalBeforeVAT = useMemo(() => {
    return (
      calculateVAT(
        cartItem?.product?.priceV2?.price || 0,
        cartItem?.product?.priceV2?.vat || 0,
        cartItem?.product?.priceV2?.vatIncluded || false,
      ).beforeVat * (cartItem?.quantity || 0)
    );
  }, [cartItem]);

  const totalAfterVat = useMemo(() => {
    const totalAfterDiscountExcludedVAT = totalBeforeVAT - itemDiscountBeforeVat;
    const totalVATValue = Math.round((totalAfterDiscountExcludedVAT * (cartItem?.product?.priceV2?.vat || 0)) / 100);
    return totalAfterDiscountExcludedVAT + totalVATValue;
  }, [totalBeforeVAT, itemDiscountBeforeVat, cartItem]);

  return (
    <ProductItemContainer isLastItem={isLastItem} isFinalStep={isFinalStep}>
      <ProductItemInfoWrapper>
        <ProductLink to={pdpUrl}>
          <ProductItemImage isFinalStep={isFinalStep}>
            <ItemImage
              isFinalStep={isFinalStep}
              noImage={!cartItem?.product?.imageUrl}
              src={cartItem?.product?.imageUrl || noImage}
              alt={cartItem?.product?.name || name}
            />
            {frameTagImage && <FrameImage src={frameTagImage} />}
          </ProductItemImage>
        </ProductLink>
        <ProductInfoDetailContainer>
          <ProductLink to={pdpUrl}>
            <ProductName isFinalStep={isFinalStep}>{cartItem?.product?.name}</ProductName>
          </ProductLink>
          <ProductUom isFinalStep={isFinalStep}>{uomInfo}</ProductUom>
          <ProductPrice>
            <Money>
              {
                calculateVAT(
                  cartItem?.product?.priceV2?.price || 0,
                  cartItem?.product?.priceV2?.vat || 0,
                  !!cartItem?.product?.priceV2?.vatIncluded,
                ).beforeVat
              }
            </Money>
            {!!cartItem?.product?.priceV2?.originalPrice &&
              cartItem?.product?.priceV2?.originalPrice > cartItem?.product?.priceV2?.price && (
                <ProductOriginalPrice>
                  <Money>
                    {
                      calculateVAT(
                        cartItem?.product?.priceV2?.originalPrice || 0,
                        cartItem?.product?.priceV2?.vat || 0,
                        !!cartItem?.product?.priceV2?.vatIncluded,
                      ).beforeVat
                    }
                  </Money>
                </ProductOriginalPrice>
              )}
          </ProductPrice>
          {!isFinalStep && loggedIn ? (
            <ProductNoteContainer onClick={openNote}>
              <NoteIcon />
              <ProductNote>{cartItem?.product?.note || translations(messages?.note)}</ProductNote>
            </ProductNoteContainer>
          ) : null}
        </ProductInfoDetailContainer>
      </ProductItemInfoWrapper>
      {isFinalStep ? (
        <QuantityValue>{cartItem?.quantity}</QuantityValue>
      ) : (
        <ProductQuantityContainer>
          <QuantityInput
            product={cartItem?.product as Product}
            size="small"
            min={0.01}
            allowDecimal={allowDecimalQuantity(cartItem?.product?.uom)}
            addFrom={TrackingAddToCartAddFromType.CartProduct}
            isFullWidth={true}
            isShowTotalMoney={false}
          />
        </ProductQuantityContainer>
      )}
      <MoneyBeforeVAT isFinalStep={isFinalStep}>
        <Money>{totalBeforeVAT}</Money>
      </MoneyBeforeVAT>
      <TotalAfterVATContainer isFinalStep={isFinalStep}>
        <TotalAfterVAT>
          <Money>{totalAfterVat}</Money>
        </TotalAfterVAT>
        <VATText>(VAT: {cartItem?.product?.vat || 0}%)</VATText>
      </TotalAfterVATContainer>
      {!isFinalStep && !!onRemoveProduct && (
        <DeleteProductContainer onClick={() => onRemoveProduct(cartItem)}>
          <SVGIcon src={DeleteIcon} renderAsImage={false} />
        </DeleteProductContainer>
      )}
    </ProductItemContainer>
  );
};

export default CartItemDesktop;
